import { useState } from "react";
import { Controller } from "react-hook-form";
import { Calendar as CalendarIcon, Copy, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getCategoryCodeSegment } from "../lib/category-tree";
import { cn, formatCategoryName, normalizeCategoryName, parseCurrency } from "../lib/utils";
import { NumericMask } from "./NumericMask";

import { useTransactionForm } from "../hooks/useTransactionForm";
import { TransactionCombobox } from "./TransactionCombobox";
import { RepeatTransactionButton, RepeatTransactionDialog } from "./RepeatTransactionDialog";
import { DetailedItemsEditor } from "./DetailedItemsEditor";
import { TRANSACTION_KINDS } from "../lib/transaction-types";

export const AddTransactionDialog = ({
  open,
  onOpenChange,
  editData = null,
  initialData = null,
  onDelete,
  onClone,
  deleteLabel = "Excluir",
}) => {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [destinationAccountOpen, setDestinationAccountOpen] = useState(false);
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteScopeOpen, setDeleteScopeOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [childCategoryParent, setChildCategoryParent] = useState(null);
  const [childCategoryDetailId, setChildCategoryDetailId] = useState(null);
  const [childCategoryName, setChildCategoryName] = useState("");

  const {
    form, type, transactionKind, setTransactionKind,
    categoryValue, setCategoryValue,
    categoryIdValue, setCategoryIdValue,
    accountValue, setAccountValue,
    destinationAccountValue, setDestinationAccountValue,
    detailedItems, setDetailedItems,
    detailDescriptionSuggestions,
    confirmed, setConfirmed,
    reconciled, setReconciled,
    recurrenceType, setRecurrenceType,
    recurrenceFrequency, setRecurrenceFrequency,
    recurrenceInterval, setRecurrenceInterval,
    installmentCount, setInstallmentCount,
    installmentStartIndex, setInstallmentStartIndex,
    amountMode, setAmountMode,
    fixedTotalEnabled, setFixedTotalEnabled,
    fixedTotalCount, setFixedTotalCount,
    onSubmit, categories, accounts,
    pendingSubmitData, confirmScope, createChildCategory, closeDialog
  } = useTransactionForm(open, onOpenChange, editData, initialData);

  const isInstallment = recurrenceType === "installment";
  const isFixed = recurrenceType === "fixed";
  const isTransfer = transactionKind === TRANSACTION_KINDS.TRANSFER;
  const isDetailed = transactionKind === TRANSACTION_KINDS.DETAILED;

  const { register, control, watch, formState: { errors, isSubmitting } } = form;
  
  const selectedDate = watch("date");
  const amount = watch("amount");
  const notes = watch("notes") || "";
  const installmentTotal = Math.max(2, parseInt(installmentCount, 10) || 2);
  const installmentStart = Math.min(Math.max(1, parseInt(installmentStartIndex, 10) || 1), installmentTotal);
  const installmentRemaining = installmentTotal - installmentStart + 1;
  const fixedTotal = Math.max(1, parseInt(fixedTotalCount, 10) || 1);
  const parsedAmount = parseCurrency(amount);
  const detailTargetAmount = isDetailed && isInstallment && amountMode === "total"
    ? parsedAmount / installmentTotal
    : parsedAmount;
  const repeatSettings = {
    recurrenceType,
    recurrenceFrequency,
    recurrenceInterval,
    installmentCount,
    installmentStartIndex,
    amountMode,
    fixedTotalEnabled,
    fixedTotalCount,
  };
  const submitLabel = (() => {
    if (isSubmitting) return "Salvando...";
    if (editData) return "Atualizar";
    if (isTransfer && recurrenceType === "installment") return `Criar ${installmentRemaining} transferência${installmentRemaining > 1 ? "s" : ""}`;
    if (recurrenceType === "installment") return `Criar ${installmentRemaining} parcela${installmentRemaining > 1 ? "s" : ""}`;
    if (isTransfer && recurrenceType === "fixed") return fixedTotalEnabled ? `Criar ${fixedTotal} transferência${fixedTotal > 1 ? "s" : ""}` : "Transferir até Dezembro";
    if (recurrenceType === "fixed") return fixedTotalEnabled ? `Criar ${fixedTotal} ocorrência${fixedTotal > 1 ? "s" : ""}` : "Criar até Dezembro";
    return "Adicionar";
  })();
  
  const filteredCategories = categories.filter((cat) => cat.type === type);

  const handleOpenChildCategory = (parent, detailItemId = null) => {
    setChildCategoryParent(parent);
    setChildCategoryDetailId(detailItemId);
    setChildCategoryName("");
    setCategoryOpen(false);
  };

  const handleCreateChildCategory = async (event) => {
    event.preventDefault();
    const result = await createChildCategory(childCategoryParent, childCategoryName, {
      selectCategory: !childCategoryDetailId,
    });
    if (result?.success === false) return;
    if (childCategoryDetailId && result?.category) {
      setDetailedItems(detailedItems.map((item) => (
        item.id === childCategoryDetailId
          ? { ...item, description: normalizeCategoryName(result.category.name) }
          : item
      )));
    }
    setChildCategoryParent(null);
    setChildCategoryDetailId(null);
    setChildCategoryName("");
  };

  const handleDelete = async (scope = "current") => {
    if (!editData || !onDelete) return;

    setIsDeleting(true);
    const result = await onDelete(editData, scope);
    setIsDeleting(false);

    if (result?.success === false) return;

    setDeleteConfirmOpen(false);
    setDeleteScopeOpen(false);
    closeDialog();
  };

  const requestDelete = () => {
    if (editData?.recurrence_id) {
      setDeleteScopeOpen(true);
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleClone = () => {
    if (!editData || !onClone) return;
    onClone(editData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden p-4 sm:p-6",
          isDetailed || isTransfer ? "sm:max-w-2xl" : "sm:max-w-lg"
        )}
      >
        <DialogHeader>
          <DialogTitle>{editData ? "Editar Transação" : "Nova Transação"}</DialogTitle>
          <DialogDescription className="sr-only">
            Preencha os dados da transação, selecione categoria e conta, e salve o lançamento.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo do lançamento</Label>
            <Select value={transactionKind} onValueChange={setTransactionKind}>
              <SelectTrigger data-testid="transaction-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TRANSACTION_KINDS.EXPENSE}>Despesa</SelectItem>
                <SelectItem value={TRANSACTION_KINDS.INCOME}>Receita</SelectItem>
                <SelectItem value={TRANSACTION_KINDS.TRANSFER}>Transferência</SelectItem>
                <SelectItem value={TRANSACTION_KINDS.DETAILED}>Detalhado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_170px]">
            <div className="space-y-2">
              <Label htmlFor="amount">Valor Total (R$)</Label>
              <Controller
                control={control}
                name="amount"
                render={({ field: { ref, ...field } }) => (
                  <NumericMask
                    {...field}
                    customRef={ref}
                    id="amount"
                    placeholder="0,00"
                    data-testid="transaction-amount"
                  />
                )}
              />
              {errors.amount && <p className="text-sm text-destructive">{errors.amount.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmed-main">Conciliado</Label>
              <div className="flex h-10 items-center justify-between gap-3 rounded-md border px-3">
                <span className="text-sm text-muted-foreground">
                  {confirmed ? "Sim" : "Pendente"}
                </span>
                <Switch id="confirmed-main" checked={confirmed} onCheckedChange={setConfirmed} />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border/80 p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "h-[62px] w-full justify-start px-3 text-left font-normal",
                        selectedDate ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : "text-muted-foreground"
                      )}
                      aria-label={isInstallment ? "Data da primeira parcela" : isFixed ? "Data da primeira ocorrência" : "Data"}
                      data-testid="transaction-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      <span className="truncate">
                        {selectedDate ? format(selectedDate, "PPP", { locale: ptBR }) : "Selecione a data"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => form.setValue("date", date)}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                {isInstallment && (
                  <p className="text-xs text-muted-foreground">
                    Serão criadas {installmentRemaining} parcela{installmentRemaining > 1 ? "s" : ""} a partir desta data
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <RepeatTransactionButton
                  settings={repeatSettings}
                  onClick={() => setRepeatOpen(true)}
                  className="h-[62px]"
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Input
              id="description"
              placeholder={isTransfer ? "Ex: Reserva, Pix entre contas..." : "Ex: Supermercado, Salário..."}
              onInput={(event) => {
                event.currentTarget.value = event.currentTarget.value.toLocaleUpperCase("pt-BR");
              }}
              {...register("description")}
              data-testid="transaction-description"
            />
            {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
          </div>
          
          <TransactionCombobox
            label={isTransfer ? "Categoria (opcional)" : "Categoria"}
            value={categoryValue}
            setValue={setCategoryValue}
            selectedId={categoryIdValue}
            setSelectedId={setCategoryIdValue}
            items={filteredCategories}
            open={categoryOpen}
            setOpen={setCategoryOpen}
            placeholder="Selecione ou digite uma categoria"
            testId="transaction-category"
            showColor={true}
            onAddChild={handleOpenChildCategory}
          />
          
          <TransactionCombobox
            label={isTransfer ? "Conta Origem" : "Conta"}
            value={accountValue}
            setValue={setAccountValue}
            items={accounts}
            open={accountOpen}
            setOpen={setAccountOpen}
            placeholder="Selecione ou digite uma conta"
            testId="transaction-account"
          />

          {isTransfer && (
            <TransactionCombobox
              label="Conta Destino"
              value={destinationAccountValue}
              setValue={setDestinationAccountValue}
              items={accounts}
              open={destinationAccountOpen}
              setOpen={setDestinationAccountOpen}
              placeholder="Selecione ou digite a conta destino"
              testId="transaction-destination-account"
            />
          )}

          {isDetailed && (
            <>
              <DetailedItemsEditor
                items={detailedItems}
                onChange={setDetailedItems}
                targetAmount={detailTargetAmount}
                descriptionSuggestions={detailDescriptionSuggestions}
                categories={filteredCategories}
                onAddChild={handleOpenChildCategory}
              />

              <div className="space-y-2">
                <Label htmlFor="notes">Observação</Label>
                <Textarea
                  id="notes"
                  maxLength={400}
                  placeholder="Observação"
                  {...register("notes")}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{errors.notes?.message}</span>
                  <span>{notes.length}/400</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <Label htmlFor="confirmed" className="text-base font-semibold">
                    Confirmado
                  </Label>
                  <Switch id="confirmed" checked={confirmed} onCheckedChange={setConfirmed} />
                </div>
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <Label htmlFor="reconciled" className="text-base font-semibold">
                    Conciliado
                  </Label>
                  <Switch id="reconciled" checked={reconciled} onCheckedChange={setReconciled} />
                </div>
              </div>
            </>
          )}
          
          {editData && (onClone || onDelete) && (
            <div className={cn("grid gap-2 pt-2", onClone && onDelete && "sm:grid-cols-2")}>
              {onClone && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleClone}
                  disabled={isSubmitting || isDeleting}
                  data-testid="clone-transaction-from-dialog"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Clonar/Repetir
                </Button>
              )}
              {onDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={requestDelete}
                  disabled={isSubmitting || isDeleting}
                  data-testid="delete-transaction-from-dialog"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {deleteLabel}
                </Button>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 btn-glow" disabled={isSubmitting} data-testid="transaction-submit">
              {submitLabel}
            </Button>
          </div>
        </form>
      </DialogContent>

      <RepeatTransactionDialog
        open={repeatOpen}
        onOpenChange={setRepeatOpen}
        amount={amount}
        recurrenceType={recurrenceType}
        setRecurrenceType={setRecurrenceType}
        recurrenceFrequency={recurrenceFrequency}
        setRecurrenceFrequency={setRecurrenceFrequency}
        recurrenceInterval={recurrenceInterval}
        setRecurrenceInterval={setRecurrenceInterval}
        installmentCount={installmentCount}
        setInstallmentCount={setInstallmentCount}
        installmentStartIndex={installmentStartIndex}
        setInstallmentStartIndex={setInstallmentStartIndex}
        amountMode={amountMode}
        setAmountMode={setAmountMode}
        fixedTotalEnabled={fixedTotalEnabled}
        setFixedTotalEnabled={setFixedTotalEnabled}
        fixedTotalCount={fixedTotalCount}
        setFixedTotalCount={setFixedTotalCount}
      />

      <Dialog open={!!childCategoryParent} onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setChildCategoryParent(null);
          setChildCategoryDetailId(null);
          setChildCategoryName("");
        }
      }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Nova categoria filha</DialogTitle>
            <DialogDescription className="sr-only">
              Informe o nome da nova subcategoria para a categoria selecionada.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateChildCategory} className="space-y-4">
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <span className="block text-xs text-muted-foreground">Categoria pai</span>
              <span className="mt-1 block truncate font-medium">
                {childCategoryParent ? `${getCategoryCodeSegment(childCategoryParent)} - ${formatCategoryName(childCategoryParent.name)}` : ""}
              </span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="child-category-name">Nome da categoria filha</Label>
              <Input
                id="child-category-name"
                value={childCategoryName}
                onChange={(event) => setChildCategoryName(event.target.value.toLocaleUpperCase("pt-BR"))}
                autoComplete="off"
                autoFocus
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setChildCategoryParent(null);
                  setChildCategoryDetailId(null);
                  setChildCategoryName("");
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 btn-glow">
                Adicionar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Recurrence Edit Modal */}
      {pendingSubmitData && (
        <Dialog open={true} onOpenChange={(val) => { if (!val) closeDialog(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar transação recorrente</DialogTitle>
              <DialogDescription className="sr-only">
                Escolha se a alteração será aplicada somente nesta transação ou em mais ocorrências da série.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 py-4">
              <p className="text-sm text-muted-foreground pb-2">
                Esta transação faz parte de uma série. Como você deseja aplicar esta alteração?
              </p>
              <Button variant="outline" className="justify-start h-auto p-4" onClick={() => confirmScope("current")}>
                <div className="text-left">
                  <div className="font-semibold">Somente esta</div>
                  <div className="text-xs text-muted-foreground font-normal">Apenas a transação selecionada será alterada.</div>
                </div>
              </Button>
              <Button variant="outline" className="justify-start h-auto p-4" onClick={() => confirmScope("future")}>
                <div className="text-left">
                  <div className="font-semibold">Esta e as próximas</div>
                  <div className="text-xs text-muted-foreground font-normal">Altera esta transação e todas as futuras da mesma série.</div>
                </div>
              </Button>
              <Button variant="outline" className="justify-start h-auto p-4" onClick={() => confirmScope("all")}>
                <div className="text-left">
                  <div className="font-semibold">Todas as transações</div>
                  <div className="text-xs text-muted-foreground font-normal">Altera todas as transações desta série, incluindo passadas.</div>
                </div>
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={closeDialog}>Cancelar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={deleteScopeOpen} onOpenChange={setDeleteScopeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir transação recorrente</DialogTitle>
            <DialogDescription>
              Escolha se a exclusão será aplicada somente nesta transação ou em mais ocorrências da série.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <p className="text-sm text-muted-foreground">
              Esta transação faz parte de uma série. Como você deseja aplicar esta exclusão?
            </p>
            <Button variant="outline" className="h-auto justify-start p-4" onClick={() => handleDelete("current")} disabled={isDeleting}>
              <div className="text-left">
                <div className="font-semibold">Somente esta</div>
                <div className="text-xs font-normal text-muted-foreground">Apenas a transação selecionada será Excluida.</div>
              </div>
            </Button>
            <Button variant="outline" className="h-auto justify-start p-4" onClick={() => handleDelete("future")} disabled={isDeleting}>
              <div className="text-left">
                <div className="font-semibold">Esta e as próximas</div>
                <div className="text-xs font-normal text-muted-foreground">Excluir esta transação e todas as futuras da mesma série.</div>
              </div>
            </Button>
            <Button variant="outline" className="h-auto justify-start p-4" onClick={() => handleDelete("all")} disabled={isDeleting}>
              <div className="text-left">
                <div className="font-semibold">Todas as transações</div>
                <div className="text-xs font-normal text-muted-foreground">Excluir todas as transações desta série, incluindo passadas.</div>
              </div>
            </Button>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteScopeOpen(false)} disabled={isDeleting}>Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir transação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A transação será removida permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
